import { InputBarContext } from "@app/components/assistant/conversation/input_bar/InputBarContext";
import { useConversationDrafts } from "@app/components/assistant/conversation/input_bar/useConversationDrafts";
import { UserSettingsPopover } from "@app/components/UserSettingsPopover";
import { WorkspacePickerRadioGroup } from "@app/components/WorkspacePicker";
import { useCreateConversationWithMessage } from "@app/hooks/useCreateConversationWithMessage";
import { useDevMode } from "@app/hooks/useDevMode";
import { useSendNotification } from "@app/hooks/useNotification";
import { usePrivacyMask } from "@app/hooks/usePrivacyMask";
import config from "@app/lib/api/config";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useSubmitFunction } from "@app/lib/client/utils";
import {
  forceUserRole,
  sendOnboardingConversation,
  showDebugTools,
} from "@app/lib/development";
import { clientFetch } from "@app/lib/egress/client";
import { serializeMention } from "@app/lib/mentions/format";
import { ConversationsUpdatedEvent } from "@app/lib/notifications/events";
import { useAppRouter } from "@app/lib/platform";
import { useUser } from "@app/lib/swr/user";
import { getConversationRoute } from "@app/lib/utils/router";
import { GLOBAL_AGENTS_SID } from "@app/types/assistant/assistant";
import type { AgentMention, MentionType } from "@app/types/assistant/mentions";
import { isAgentMention } from "@app/types/assistant/mentions";
import type { SubscriptionType } from "@app/types/plan";
import type { UserTypeWithWorkspaces, WorkspaceType } from "@app/types/user";
import {
  isOnlyAdmin,
  isOnlyBuilder,
  isOnlyManager,
  isOnlyUser,
} from "@app/types/user";
import { datadogLogs } from "@datadog/browser-logs";
import {
  Avatar,
  Beaker02,
  BookOpen01,
  ChevronDown,
  ChromeLogo,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Eye,
  EyeOff,
  FirefoxLogo,
  Heart,
  Icon,
  Lightbulb04,
  LogOut01,
  MessageChatCircle,
  MessagePlusCircle,
  MessageTextCircle01,
  Plus,
  Separator,
  Shapes,
  SlackLogo,
  Star01,
  Terminal,
  User01,
} from "@dust-tt/sparkle";
import { useCallback, useContext, useMemo, useState } from "react";

interface UserMenuProps {
  user: UserTypeWithWorkspaces;
  owner: WorkspaceType;
  subscription: SubscriptionType | null;
}

export function UserMenu({ user, owner, subscription }: UserMenuProps) {
  const router = useAppRouter();
  const { featureFlags } = useFeatureFlags();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const sendNotification = useSendNotification();
  const devMode = useDevMode();
  const privacyMask = usePrivacyMask();
  const { clearAllDraftsFromUser } = useConversationDrafts({
    workspaceId: owner.sId,
    userId: user.sId,
    draftKey: "user-menu",
  });

  const createConversationWithMessage = useCreateConversationWithMessage({
    owner,
    user,
  });

  const { setSelectedAgent } = useContext(InputBarContext);

  const handleAskHelp = () => {
    if (
      router.pathname === "/w/[wId]/conversation/[cId]" ||
      router.pathname.match(/^\/w\/[^/]+\/conversation\/[^/]+$/)
    ) {
      setSelectedAgent({
        type: "agent",
        id: GLOBAL_AGENTS_SID.HELPER,
        label: "Help",
        pictureUrl: "/static/systemavatar/helper_avatar_full.png",
        description: "Help on how to use Uday",
      });
    } else {
      void router.push(
        getConversationRoute(
          owner.sId,
          "new",
          `agent=${GLOBAL_AGENTS_SID.HELPER}`
        )
      );
    }
  };

  const { submit: handleHelpSubmit } = useSubmitFunction(
    useCallback(
      async (input: string, mentions: MentionType[]) => {
        const inputWithHelp = input.includes("@help")
          ? input
          : `@help ${input.trimStart()}`;
        const mentionsWithHelp = mentions.some(
          (mention) =>
            isAgentMention(mention) &&
            mention.configurationId === GLOBAL_AGENTS_SID.HELPER
        )
          ? mentions
          : [
              ...mentions,
              { configurationId: GLOBAL_AGENTS_SID.HELPER } as AgentMention,
            ];
        const conversationRes = await createConversationWithMessage({
          messageData: {
            input: inputWithHelp.replace(
              "@help",
              serializeMention({ name: "help", sId: GLOBAL_AGENTS_SID.HELPER })
            ),
            mentions: mentionsWithHelp,
            contentFragments: {
              uploaded: [],
              contentNodes: [],
            },
          },
        });
        if (conversationRes.isErr()) {
          sendNotification({
            title: conversationRes.error.title,
            description: conversationRes.error.message,
            type: "error",
          });
        } else {
          void router.push(
            getConversationRoute(owner.sId, conversationRes.value.sId)
          );
        }
      },
      [createConversationWithMessage, owner, router, sendNotification]
    )
  );

  const isFirefox =
    typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);

  const forceRoleUpdate = useMemo(
    () => async (role: "user" | "builder" | "admin" | "manager") => {
      const result = await forceUserRole(user, owner, role, featureFlags);
      if (result.isOk()) {
        sendNotification({
          title: "Success !",
          description: result.value + " (reloading...)",
          type: "success",
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        sendNotification({
          title: "Error !",
          description: result.error,
          type: "error",
        });
      }
    },
    [owner, sendNotification, user, featureFlags]
  );

  const handleSendOnboarding = useMemo(
    () => async () => {
      const result = await sendOnboardingConversation(owner, featureFlags);
      if (result.isOk) {
        window.dispatchEvent(new ConversationsUpdatedEvent());
        sendNotification({
          title: "Success !",
          description: "Onboarding conversation created (redirecting...)",
          type: "success",
        });
        setTimeout(() => {
          void router.push(
            `/w/${owner.sId}/conversation/${result.conversationId}`
          );
        }, 1000);
      } else {
        sendNotification({
          title: "Error !",
          description: result.error,
          type: "error",
        });
      }
    },
    [owner, sendNotification, featureFlags, router]
  );

  // Workspaces/organizations come from GET /api/user (the workspace auth
  // context's user object does not carry them). While it loads, fall back to
  // the prop — the section simply stays hidden until the data arrives.
  const { user: fetchedUser, mutateUser } = useUser();
  const userWithWorkspaces = fetchedUser ?? user;
  const organizations = userWithWorkspaces.organizations ?? [];
  const workspaces = userWithWorkspaces.workspaces ?? [];

  const hasMultipleWorkspaces =
    organizations.length > 1 || workspaces.length > 1;

  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const handleCreateWorkspace = useMemo(
    () => async () => {
      setIsCreatingWorkspace(true);
      try {
        const response = await clientFetch("/api/create-new-workspace", {
          method: "POST",
        });
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          const description =
            body?.api_error?.message ?? "Failed to create the workspace.";
          sendNotification({
            title: "Error !",
            description,
            type: "error",
          });
          return;
        }
        await mutateUser();
        const sId: string | undefined = body?.sId;
        if (sId) {
          void router.push(`/w/${sId}/`);
        } else {
          window.location.reload();
        }
      } finally {
        setIsCreatingWorkspace(false);
      }
    },
    [mutateUser, router, sendNotification]
  );

  return (
    <>
      <UserSettingsPopover
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        owner={owner}
      />
      <DropdownMenu>
        <DropdownMenuTrigger className="hover:bg-hover data-[state=open]:bg-selected rounded-xl p-2 m-2">
          <div className="group flex cursor-pointer items-center justify-between gap-2">
            <span className="sr-only">Open user menu</span>
            <div className="flex gap-2 items-center">
              <Avatar
                size="sm"
                visual={
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  user.image
                    ? user.image
                    : "https://gravatar.com/avatar/anonymous?d=mp"
                }
                clickable
                isRounded
              />
              <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                <span
                  className={cn(
                    "heading-sm w-full truncate transition-colors",
                    "text-foreground"
                  )}
                >
                  {user.firstName}
                </span>
                <span className="-mt-0.5 w-full truncate text-sm text-muted-foreground">
                  {owner.name}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Icon
                visual={ChevronDown}
                className="text-muted-foreground group-hover:text-primary-400 group-active:text-primary-950"
              />
            </div>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="end"
          sideOffset={8}
          className="w-64"
        >
          <DropdownMenuLabel label="Workspace" />
          {hasMultipleWorkspaces && (
            <WorkspacePickerRadioGroup
              user={userWithWorkspaces}
              workspace={owner}
            />
          )}
          <DropdownMenuItem
            label="Create workspace"
            icon={Plus}
            disabled={isCreatingWorkspace}
            onClick={() => void handleCreateWorkspace()}
          />
          <Separator className="my-1" />

          <DropdownMenuSub>
            <DropdownMenuSubTrigger label="Help" icon={Heart} />
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuLabel label="Learn about Uday" />
                <DropdownMenuItem
                  label="Guides & Documentation"
                  icon={BookOpen01}
                  href="https://docs.dust.tt"
                  target="_blank"
                />
                <DropdownMenuItem
                  label="Join the Slack Community"
                  icon={SlackLogo}
                  href="https://dust-community.tightknit.community/join"
                  target="_blank"
                />
                <DropdownMenuLabel label="Ask questions" />
                <DropdownMenuItem
                  label="Ask @help"
                  icon={MessageChatCircle}
                  onClick={() => void handleAskHelp()}
                />
                <DropdownMenuItem
                  label="How do I invite new users?"
                  icon={MessageTextCircle01}
                  onClick={() =>
                    void handleHelpSubmit("How do I invite new users?", [])
                  }
                />
                <DropdownMenuItem
                  label="How do I use agents in Slack workflow?"
                  icon={MessageTextCircle01}
                  onClick={() =>
                    void handleHelpSubmit(
                      "How do I use agents in Slack workflow?",
                      []
                    )
                  }
                />
                <DropdownMenuItem
                  label="How do I manage billing?"
                  icon={MessageTextCircle01}
                  onClick={() =>
                    void handleHelpSubmit("How do I manage billing?", [])
                  }
                />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuItem
            label="Academy"
            icon={BookOpen01}
            href="https://dust.tt/academy"
            target="_blank"
          />

          {isFirefox ? (
            <DropdownMenuItem
              label="Firefox extension"
              icon={FirefoxLogo}
              href="https://addons.mozilla.org/firefox/addon/dust/"
              target="_blank"
            />
          ) : (
            <DropdownMenuItem
              label="Chrome extension"
              icon={ChromeLogo}
              href="https://chromewebstore.google.com/detail/dust/fnkfcndbgingjcbdhaofkcnhcjpljhdn"
              target="_blank"
            />
          )}

          {subscription?.plan.limits.canUseProduct && (
            <>
              <DropdownMenuItem
                label="Exploratory features"
                icon={Beaker02}
                href={`/w/${owner.sId}/labs`}
              />
              <Separator className="my-1" />
            </>
          )}

          <DropdownMenuLabel label="Account" />
          {subscription?.plan.limits.canUseProduct && (
            <DropdownMenuItem
              label="Personal Settings"
              icon={User01}
              onSelect={() => setSettingsOpen(true)}
            />
          )}

          <DropdownMenuItem
            label="Sign&nbsp;out"
            icon={LogOut01}
            onClick={() => {
              // Clear all conversation drafts for this user.
              clearAllDraftsFromUser();

              datadogLogs.clearUser();
              window.DD_RUM?.onReady(() => {
                window.DD_RUM?.clearUser();
              });
              window.location.href = `${config.getApiBaseUrl()}/api/workos/logout`;
            }}
          />

          {showDebugTools(featureFlags) && (
            <>
              <Separator className="my-1" />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger label="Dev Tools" icon={Shapes} />
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {(router.pathname === "/w/[wId]/conversation/[cId]" ||
                      router.pathname.match(
                        /^\/w\/[^/]+\/conversation\/[^/]+$/
                      )) && (
                      <DropdownMenuItem
                        label="Debug conversation"
                        onClick={() => {
                          const regexp = new RegExp(
                            `/w/([^/]+)/conversation/([^/]+)`
                          );
                          const match = window.location.href.match(regexp);
                          if (match) {
                            window.open(
                              `/poke/${match[1]}/conversation/${match[2]}`,
                              "_blank"
                            );
                          }
                        }}
                        icon={Shapes}
                      />
                    )}
                    {!isOnlyAdmin(owner) && (
                      <DropdownMenuItem
                        label="Become Admin"
                        onClick={() => forceRoleUpdate("admin")}
                        icon={Star01}
                      />
                    )}
                    {!isOnlyManager(owner) && (
                      <DropdownMenuItem
                        label="Become Manager"
                        onClick={() => forceRoleUpdate("manager")}
                        icon={Star01}
                      />
                    )}
                    {!isOnlyBuilder(owner) && (
                      <DropdownMenuItem
                        label="Become Builder"
                        onClick={() => forceRoleUpdate("builder")}
                        icon={Lightbulb04}
                      />
                    )}
                    {!isOnlyUser(owner) && (
                      <DropdownMenuItem
                        label="Become User"
                        onClick={() => forceRoleUpdate("user")}
                        icon={User01}
                      />
                    )}
                    <DropdownMenuItem
                      label={`${privacyMask.isEnabled ? "Disable" : "Enable"} Privacy Mask`}
                      onClick={privacyMask.toggle}
                      icon={privacyMask.isEnabled ? EyeOff : Eye}
                    />
                    <DropdownMenuItem
                      label={`${devMode.isEnabled ? "Disable" : "Enable"} Dev Console`}
                      onClick={devMode.toggle}
                      icon={Terminal}
                    />
                    {owner.role === "admin" && (
                      <DropdownMenuItem
                        label="Send onboarding conversation"
                        onClick={handleSendOnboarding}
                        icon={MessagePlusCircle}
                      />
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
